import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { CommonsProvider, useCommons } from "./lib/store";
import { recordVisit } from "./lib/api";
import { TourProvider } from "./tour/TourProvider";
import TourOverlay from "./tour/TourOverlay";
import ScrollMemory from "./components/ScrollMemory";
import DemoBadge from "./components/DemoBadge";
import Home from "./pages/Home";
import Create from "./pages/Create";
import ProjectDetail from "./pages/ProjectDetail";
import Manage from "./pages/Manage";
import Category from "./pages/Category";
import Roots from "./pages/Roots";
import Story from "./pages/Story";
import Profile from "./pages/Profile";
import ProjectLibrary from "./pages/ProjectLibrary";
import DemoFresh from "./pages/DemoFresh";
import DevRequirementFixture from "./pages/DevRequirementFixture";
import Admin from "./pages/Admin";
import Paused from "./pages/Paused";
import About from "./pages/About";
import "./App.css";

// The demo has no login. If the user is still being auto-provisioned we
// briefly show a loader; otherwise every route renders immediately.
function Gate({ children }) {
  const { user, loading } = useCommons();
  if (loading || user === undefined || user === null) {
    return <div className="c-load" data-testid="app-loading">Opening Commons…</div>;
  }
  return children;
}

export default function App() {
  useEffect(() => {
    // Visitor analytics beacon. Fires once per App mount. Skipped on
    // /admin so operator traffic never inflates public visitor
    // counts. Server-side dedup means multiple beacon fires from
    // the same browser in the same day are idempotent — so the
    // paused-redirect double-mount (public URL → /paused) still
    // resolves to exactly one visitor.
    try {
      const path = window.location.pathname || "";
      if (!path.startsWith("/admin")) {
        recordVisit();
      }
    } catch { /* best-effort */ }
  }, []);
  return (
    <CommonsProvider>
      <BrowserRouter>
        <ScrollMemory />
        <TourProvider>
          <div className="c-phone-frame">
            <div className="c-phone-shell">
              <DemoBadge />
              <Routes>
                <Route path="/" element={<Gate><Home /></Gate>} />
                <Route path="/create" element={<Gate><Create /></Gate>} />
                <Route path="/project/:id" element={<Gate><ProjectDetail /></Gate>} />
                <Route path="/project/:id/manage" element={<Gate><Manage /></Gate>} />
                <Route path="/commons/:categoryId" element={<Gate><Category /></Gate>} />
                <Route path="/roots/:id" element={<Gate><Roots /></Gate>} />
                <Route path="/roots/:id/:personId" element={<Gate><Roots /></Gate>} />
                <Route path="/story/:id" element={<Gate><Story /></Gate>} />
                <Route path="/profile" element={<Gate><Profile /></Gate>} />
                <Route path="/library" element={<Gate><ProjectLibrary /></Gate>} />
                {/* Shareable "always fresh" demo URL — wipes state and
                    auto-opens the tour. Rendered outside <Gate> so it
                    doesn't wait on the provisioning it's about to blow away. */}
                <Route path="/demo/fresh" element={<DemoFresh />} />
                {/* Commons Admin v1 — operator-only panel. Deliberately
                    NOT wrapped in <Gate>: Admin must be reachable even
                    while Commons is paused, and it never depends on
                    visitor onboarding. Server-side auth is enforced. */}
                <Route path="/admin" element={<Admin />} />
                {/* Paused screen shown when the site is globally paused.
                    Also outside <Gate> — the visitor doesn't need a
                    session to see it. */}
                <Route path="/paused" element={<Paused />} />
                {/* About — public, no session required. Rendered outside
                    <Gate> so it loads instantly and remains reachable even
                    while Commons is paused or still provisioning. */}
                <Route path="/about" element={<About />} />
                {/* Development-only preview for verifying the requirement
                    description UX at various lengths. Gated on
                    NODE_ENV so production builds don't expose it. Not
                    linked from anywhere in the app. */}
                {process.env.NODE_ENV !== "production" ? (
                  <Route path="/dev/req-descriptions" element={<DevRequirementFixture />} />
                ) : null}
                {/* Old onboarding route retired — send legacy links home. */}
                <Route path="/onboard" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <TourOverlay />
            </div>
          </div>
          <Toaster position="top-center" richColors />
        </TourProvider>
      </BrowserRouter>
    </CommonsProvider>
  );
}
